/**
 * StateLanding — public page for a state, listing all professions with CE requirements.
 * Mirrors ProfessionLanding layout: dark hero, sticky profession nav, per-profession cards.
 * URL: /states/:stateSlug
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStateBySlug, getProfessionById } from '../constants/statesData';
import {
  getStateRequirementsForState,
  IStateRequirement,
} from '../api/StateRequirements';
import SEOHelmet from '../components/SEOHelmet';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function cycleLabel(req: IStateRequirement): string {
  if (req.hasFixedCycleDates && req.fixedCycleStartMonth && req.fixedCycleEndMonth) {
    const start = MONTH_NAMES[(req.fixedCycleStartMonth ?? 1) - 1];
    const end   = MONTH_NAMES[(req.fixedCycleEndMonth   ?? 12) - 1];
    return `${start} – ${end} (${req.cycleLengthYears}-yr cycle)`;
  }
  return `${req.cycleLengthYears}-year rolling cycle`;
}

function slugify(str: string): string {
  return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

// ── Sub-components ────────────────────────────────────────────────────────────
// Depth hierarchy: page=paper → card=white → StatPill=surface-muted (darker, recessed)
const StatPill = ({ label, value, accent = 'var(--color-accent)' }: { label: string; value: string; accent?: string }) => (
  <div style={{
    padding: '0.875rem 1.25rem',
    background: 'var(--color-surface-muted)',
    border: `1px solid ${accent}22`,
    borderTop: `3px solid ${accent}`,
    borderRadius: 'var(--radius-md)',
    minWidth: '130px',
    flex: '1',
  }}>
    <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>{label}</div>
    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xl)', color: 'var(--color-text)', lineHeight: 1 }}>{value}</div>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const StateLanding: React.FC = () => {
  const { stateSlug } = useParams<{ stateSlug: string }>();
  const stateData = stateSlug ? getStateBySlug(stateSlug) : undefined;

  const [requirements, setRequirements] = useState<IStateRequirement[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(false);
  const [activeProfId, setActiveProfId] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!stateData) { setLoading(false); return; }
    setLoading(true);
    setError(false);
    getStateRequirementsForState(stateData.stateID)
      .then(data => {
        const withNames = data.map(r => ({
          ...r,
          professionName: getProfessionById(r.professionID)?.name ?? r.professionName ?? r.professionID,
        }));
        const sorted = [...withNames].sort((a, b) =>
          (a.professionName ?? a.professionID).localeCompare(b.professionName ?? b.professionID)
        );
        setRequirements(sorted);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [stateData?.stateID]);

  useEffect(() => {
    if (!requirements.length) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveProfId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [requirements]);

  const scrollToProf = (id: string) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const mainNavHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height')) || 64;
    const profNavHeight = navRef.current?.offsetHeight ?? 0;
    const top = el.getBoundingClientRect().top + window.scrollY - mainNavHeight - profNavHeight - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  if (!stateData) {
    return (
      <>
        <SEOHelmet noindex={true} title="State Not Found | CEC Track" />
        <div style={{ padding: '5rem 1.5rem', textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-3xl)', color: 'var(--color-text)' }}>State Not Found</h1>
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)', marginBottom: '2rem' }}>The state you're looking for doesn't exist.</p>
          <Link to="/" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)' }}>← Back to Home</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHelmet
        title={`${stateData.name} CE Requirements by Profession | CEC Track`}
        description={`Continuing education requirements for licensed professionals in ${stateData.name}. Find CE credits, cycle lengths, and renewal rules for architects, engineers, CPAs, and more.`}
        keywords={`${stateData.name} continuing education, ${stateData.abbreviation} license renewal, ${stateData.name} CE requirements, professional license ${stateData.abbreviation}`}
        canonical={`https://cectrack.com/states/${stateData.slug}`}
        noindex={!loading && requirements.length === 0}
      />

      {/* ── Hero ── */}
      <section style={{
        background: 'var(--color-primary)',
        padding: '5rem 0 4rem',
      }}>

        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          {/* Breadcrumb */}
          <div style={{ marginBottom: '1rem' }}>
            <Link to="/" style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Home</Link>
            <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 0.5rem' }}>/</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-accent)' }}>{stateData.name}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
            {/* State abbreviation badge */}
            <div style={{
              width: '80px', height: '80px', flexShrink: 0,
              borderRadius: 'var(--radius-md)',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1.5rem', color: '#fff', letterSpacing: '0.05em' }}>
                {stateData.abbreviation}
              </span>
            </div>

            <div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.4rem' }}>
                CE Requirements by Profession
              </p>
              <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', color: '#fff', lineHeight: 1.05, marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>
                {stateData.name}
              </h1>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'rgba(255,255,255,0.65)', maxWidth: '560px' }}>
                Credits, cycle lengths, mandatory categories, and renewal rules for every licensed profession in {stateData.name}.
              </p>
            </div>
          </div>

          {!loading && !error && (
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '2rem' }}>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-briefcase" style={{ color: 'var(--color-accent)' }} />
                {requirements.length} {requirements.length === 1 ? 'profession' : 'professions'} in system
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-arrow-clockwise" style={{ color: 'var(--color-accent)' }} />
                Updated from live database
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Sticky anchor nav ── */}
      {!loading && requirements.length > 0 && (
        <div ref={navRef} style={{
          position: 'sticky', top: 'var(--navbar-height)', zIndex: 100,
          background: 'var(--color-surface)',
          borderBottom: '2px solid var(--color-border)',
        }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
            <div style={{ display: 'flex', gap: '0.375rem', overflowX: 'auto', padding: '0.75rem 0', scrollbarWidth: 'none' }}>
              {requirements.map(req => {
                const id = slugify(req.professionName ?? req.professionID);
                const label = req.professionID;
                const isActive = activeProfId === id;
                return (
                  <button
                    key={id}
                    onClick={() => scrollToProf(id)}
                    title={req.professionName}
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontWeight: isActive ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                      fontSize: '0.7rem',
                      padding: '0.35rem 0.65rem',
                      borderRadius: 'var(--radius-sm)',
                      border: isActive ? '1.5px solid var(--color-primary)' : '1px solid var(--color-border)',
                      background: isActive ? 'var(--color-primary)' : 'transparent',
                      color: isActive ? '#fff' : 'var(--color-text-muted)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all var(--transition-fast)',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ background: 'var(--color-surface)', minHeight: '60vh' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '3rem 1.5rem' }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '5rem 0' }}>
              <div className="spinner-border" style={{ color: 'var(--color-accent)', width: '2.5rem', height: '2.5rem' }} role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)', marginTop: '1rem', fontSize: 'var(--fs-sm)' }}>Loading profession requirements…</p>
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '5rem 0' }}>
              <i className="bi bi-exclamation-circle" style={{ fontSize: '2.5rem', color: 'var(--color-danger)', display: 'block', marginBottom: '1rem' }} />
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)', marginBottom: '1.5rem' }}>
                Could not load requirements. Please try again.
              </p>
              <Link to="/register" style={{ fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-accent)', textDecoration: 'none' }}>
                Create a free account to track your CE →
              </Link>
            </div>
          )}

          {!loading && !error && requirements.length === 0 && (
            <div style={{ textAlign: 'center', padding: '5rem 0' }}>
              <i className="bi bi-database-x" style={{ fontSize: '2.5rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '1rem' }} />
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)' }}>
                No profession requirements found for {stateData.name} yet.
              </p>
            </div>
          )}

          {/* ── Profession sections ── */}
          {!loading && !error && requirements.map((req, idx) => {
            const profName = req.professionName ?? req.professionID;
            const id = slugify(profName);
            const term = req.creditTerminology ?? 'Credits';
            const hasCategories = req.categoryIDs && req.categoryIDs.some(cat => (cat.minCredits ?? 0) > 0);

            return (
              <section
                key={req._id ?? idx}
                id={id}
                ref={el => { sectionRefs.current[id] = el; }}
                style={{
                  marginBottom: '2.5rem',
                  background: 'var(--color-surface-raised)',
                  border: '1px solid var(--color-border)',
                  borderLeft: '4px solid var(--color-primary)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-card)',
                  overflow: 'hidden',
                  scrollMarginTop: '140px',
                }}
              >
                {/* Profession header — white (same level as card) */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '1.25rem 1.5rem',
                  borderBottom: '1px solid var(--color-border)',
                  background: 'var(--color-surface-raised)',
                  flexWrap: 'wrap', gap: '0.75rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {/* Profession ID badge */}
                    <div style={{
                      width: '56px', height: '56px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--color-primary)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0,
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontWeight: 700,
                        fontSize: req.professionID.length > 3 ? '0.6rem' : '0.8rem',
                        color: '#fff', letterSpacing: '0.04em',
                      }}>
                        {req.professionID}
                      </span>
                    </div>

                    {/* Profession name */}
                    <div>
                      <h2 style={{
                        fontFamily: 'var(--font-display)', fontWeight: 700,
                        fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', color: 'var(--color-text)',
                        margin: 0, lineHeight: 1.1, letterSpacing: '-0.01em',
                      }}>
                        {profName}
                      </h2>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                        {stateData.name} — License Renewal Requirements
                      </div>
                    </div>
                  </div>

                  {/* Reference links */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {req.stateprofessionlink1 && (
                      <a href={req.stateprofessionlink1} target="_blank" rel="noopener noreferrer" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
                        color: 'var(--color-accent)', textDecoration: 'none',
                        padding: '0.4rem 0.875rem', border: '1px solid var(--color-accent)',
                        borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap',
                        transition: 'background var(--transition-fast), color var(--transition-fast)',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--color-accent)'; }}
                      >
                        <i className="bi bi-box-arrow-up-right" />
                        {req.stateprofessionlink1desc || 'Official Source'}
                      </a>
                    )}
                    {req.stateprofessionlink2 && (
                      <a href={req.stateprofessionlink2} target="_blank" rel="noopener noreferrer" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
                        color: 'var(--color-text-muted)', textDecoration: 'none',
                        padding: '0.4rem 0.875rem', border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap',
                        transition: 'border-color var(--transition-fast), color var(--transition-fast)',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-text-muted)'; e.currentTarget.style.color = 'var(--color-text)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                      >
                        <i className="bi bi-box-arrow-up-right" />
                        {req.stateprofessionlink2desc || 'Additional Resource'}
                      </a>
                    )}
                    {/* Link to full profession page */}
                    <Link
                      to={`/profession/${getProfessionById(req.professionID)?.slug ?? slugify(profName)}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
                        color: 'var(--color-text-muted)', textDecoration: 'none',
                        padding: '0.4rem 0.875rem', border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap',
                        transition: 'border-color var(--transition-fast), color var(--transition-fast)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-text-muted)'; e.currentTarget.style.color = 'var(--color-text)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                    >
                      <i className="bi bi-arrow-right-circle" />
                      All states
                    </Link>
                  </div>
                </div>

                {/* Stat pills — surface-muted = recessed inside white card */}
                <div style={{ padding: '1.25rem 1.5rem', display: 'flex', gap: '0.875rem', flexWrap: 'wrap' }}>
                  <StatPill label={`Total ${term}`}   value={String(req.totalCreditsRequired)} accent="var(--color-primary)" />
                  <StatPill label="Cycle Length"      value={`${req.cycleLengthYears} yr`}    accent="var(--color-accent)" />
                  <StatPill label="Renewal Period"    value={cycleLabel(req)}                  accent="var(--color-accent-warm)" />
                  {req.allowCarryover && (
                    <StatPill label="Carryover Max" value={req.carryoverMaxCredits ? `${req.carryoverMaxCredits} ${term}` : 'Yes'} accent="var(--color-success)" />
                  )}
                  {req.selfStudyMaxPercent != null && (
                    <StatPill label="Self-Study Max" value={`${req.selfStudyMaxPercent}%`} accent="var(--color-text-muted)" />
                  )}
                </div>

                {/* Category requirements */}
                {hasCategories && (
                  <div style={{ padding: '0 1.5rem 1.25rem' }}>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.625rem' }}>
                      Category Requirements
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {req.categoryIDs.filter(cat => (cat.minCredits ?? 0) > 0).map((cat, ci) => {
                        const isHSW    = cat.categoryTag?.toUpperCase().includes('HSW');
                        const isEthics = cat.categoryTag?.toUpperCase().includes('ETHIC');
                        const tagColor = isHSW ? 'var(--color-accent)' : isEthics ? 'var(--color-accent-warm)' : 'var(--color-text-muted)';
                        const tagBg    = isHSW ? 'var(--color-accent-light)' : isEthics ? 'var(--color-accent-warm-light)' : 'var(--color-surface-muted)';
                        return (
                          <div key={ci} style={{
                            padding: '0.4rem 0.875rem',
                            background: tagBg,
                            border: `1px solid ${tagColor}44`,
                            borderRadius: 'var(--radius-full)',
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                          }}>
                            <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: tagColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {cat.categoryTag}
                            </span>
                            {cat.minCredits != null && (
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-text)', fontWeight: 'var(--fw-semibold)' }}>
                                {cat.minCredits}
                                {cat.maxCredits != null && cat.maxCredits !== cat.minCredits ? `–${cat.maxCredits}` : ''}
                                {' '}{term}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Carryover / cycle notes */}
                {(req.allowCarryover || req.cycleDateNotes) && (
                  <div style={{ padding: '0 1.5rem 1.25rem', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    {req.allowCarryover && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-success)' }}>
                        <i className="bi bi-check-circle-fill" />
                        Carryover allowed{req.carryoverMaxCredits ? ` (max ${req.carryoverMaxCredits} ${term})` : ''}
                      </div>
                    )}
                    {req.cycleDateNotes && (
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                        <i className="bi bi-calendar-range me-1" />{req.cycleDateNotes}
                      </div>
                    )}
                  </div>
                )}

                {/* Notes — surface-muted = recessed */}
                {req.notes && (
                  <div style={{
                    margin: '0 1.5rem 1.25rem',
                    padding: '0.75rem 1rem',
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-border)',
                    borderLeft: '3px solid var(--color-accent-warm)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--color-text-muted)',
                    lineHeight: 1.65,
                  }}>
                    <i className="bi bi-info-circle me-2" style={{ color: 'var(--color-accent-warm)' }} />
                    {req.notes}
                  </div>
                )}
              </section>
            );
          })}

          {/* CTA */}
          {!loading && !error && requirements.length > 0 && (
            <div style={{
              marginTop: '3rem', padding: '2.5rem',
              background: 'var(--color-primary)',
              borderRadius: 'var(--radius-lg)',
              textAlign: 'center',
            }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xl)', color: '#fff', marginBottom: '0.75rem' }}>
                Ready to track your {stateData.name} CE credits?
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.65)', marginBottom: '1.5rem' }}>
                CEC Track automatically calculates compliance across all categories and states — for free.
              </p>
              <Link
                to="/register"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.75rem 2rem',
                  background: 'var(--color-accent)', color: 'var(--color-primary)',
                  fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-base)',
                  borderRadius: 'var(--radius-md)', textDecoration: 'none',
                  transition: 'background var(--transition-fast)',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-accent-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-accent)')}
              >
                Get Started Free <i className="bi bi-arrow-right" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StateLanding;
